import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Notebook, Gear, SignOut, Heart, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const NAV = [
  { to: "/log", label: "Logbook", Icon: Notebook, testid: "nav-log" },
  { to: "/mileage", label: "Travel & Mileage", Icon: MapPin, testid: "nav-mileage" },
  { to: "/settings", label: "Settings", Icon: Gear, testid: "nav-settings" },
];

export default function Shell() {
  const { user, isAuthed, logout } = useAuth();
  const navigate = useNavigate();

  const goLogin = () => navigate("/login");

  return (
    <div className="min-h-screen bg-[#F7F6F2] text-[#1E2420]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#F7F6F2]/90 backdrop-blur border-b border-[#E3E1D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            data-testid="brand-home"
            onClick={() => navigate("/log")}
            className="flex items-center gap-2 group"
          >
            <img src="/medeasy-logo.png" alt="MedEasy — Medical Expenses & KM Tracker · Canada" className="h-11 w-auto object-contain" />
          </button>

          <div className="flex items-center gap-2">
            <button
              data-testid="shell-hope-air-badge"
              onClick={() => navigate("/hope-air")}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#1E2420] bg-[#C17767]/10 hover:bg-[#C17767]/20 px-3 py-1.5 rounded-full transition-colors"
            >
              <Heart weight="fill" size={12} className="text-[#C17767]" />
              10% to Hope Air
            </button>
            {isAuthed ? (
              <div className="flex items-center gap-3">
                <div className="hidden sm:block text-right">
                  <div className="text-sm font-medium" data-testid="user-name">{user?.name}</div>
                  <div className="text-xs text-[#5C6660]" data-testid="user-email">{user?.email}</div>
                </div>
                <Avatar className="h-9 w-9 ring-1 ring-[#E3E1D9]">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
                </Avatar>
                <Button
                  data-testid="logout-btn"
                  variant="outline"
                  size="icon"
                  className="rounded-full border-[#E3E1D9]"
                  onClick={logout}
                  title="Sign out"
                >
                  <SignOut size={18} />
                </Button>
              </div>
            ) : (
              <Button
                data-testid="header-signin"
                onClick={goLogin}
                className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white"
              >
                Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 pt-6 pb-24 md:pb-10">
        {/* Sidebar (desktop) */}
        <aside className="hidden md:block">
          <nav className="sticky top-24 flex flex-col gap-1">
            {NAV.map(({ to, label, Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive
                      ? "bg-[#2C4C3B] text-white"
                      : "text-[#1E2420] hover:bg-[#EBE9E0]"
                  }`
                }
              >
                <Icon weight="duotone" size={20} />
                <span className="font-medium">{label}</span>
              </NavLink>
            ))}
            {!isAuthed && (
              <div className="mt-6 p-4 rounded-2xl border border-[#E3E1D9] bg-white">
                <div className="text-xs uppercase tracking-widest text-[#5C6660] mb-2">Guest mode</div>
                <p className="text-sm text-[#1E2420] mb-3">Sign in to sync your records across devices.</p>
                <Button
                  data-testid="sidebar-signin"
                  onClick={goLogin}
                  className="w-full rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white"
                >
                  Sign in
                </Button>
              </div>
            )}
          </nav>
        </aside>

        <main className="fade-in">
          <Outlet />
        </main>
      </div>

      {/* Bottom nav (mobile) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#E3E1D9]">
        <div className="grid grid-cols-3">
          {NAV.map(({ to, label, Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`m-${testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2 text-xs ${
                  isActive ? "text-[#2C4C3B]" : "text-[#5C6660]"
                }`
              }
            >
              <Icon weight="duotone" size={20} />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
